import type { Sample } from '@ctcells/contract'
import { safeStorage } from '@ctcells/core'
import { t, type MessageKey } from '@ctcells/i18n'

import { splitTags } from './sampleRows.ts'

/**
 * 시료 추가 칸의 **제안** — 고정 제안은 이 파일 한 곳에만 둔다(eric 2026-09-29 「관례적으로 많이
 * 쓰이는 포멧으로 … 정본에서 안 어긋나게」).
 *
 * 🔴 **전부 «제안»이다.** 서버(`RegisterSampleBody`)는 이 칸들을 **열거 없는 자유 문자열**로 받는다.
 *    목록 밖 값을 막지 않는다 — 막으면 계약에 없는 규칙을 화면이 발명한 것이 된다.
 * 🔴 **고정 제안은 두 부류뿐이다**(CLAUDE.md 「🔸 미확정 값은 하드코딩하지 않는다」).
 *    ⓐ 정본 · 서버 문면에 **실제로 적힌 값** ⓑ **표준 자체가 닫힌 집합**인 것.
 *    암종 목록처럼 「흔히 쓰니까」로 지어낸 목록은 두지 않는다 — 그 칸은 이미 등록된 시료에서 쓰인 값만 제안한다.
 */

/**
 * 시료 타입 — **eric 지시**(2026-10-04 「시료 타입 (테스트, 검증, 기타 …)」). 정본 문면보다 앞선다.
 * 🔴 고르기 + 직접 입력이다. 서버는 이 칸을 **자유 문자열**로 받는다 — dev2 실측 2026-10-04:
 *    DDL `sample_type TEXT`(CHECK · 어휘 표 없음) · 계약 `str | None`(enum 없음) · 서버 코드가 값으로 가르는
 *    곳 0건. ⚠ 관리자 시험 배치는 `테스트 검체` · `Reference` 를 쓴다(그 화면의 약속) — 그 값은 쓰인 값 제안으로 뒤에 선다.
 * 종전 정본 문면 「전임상 모델 시료 / 환자 혈액 시료」는 고정 제안에서 내렸다(쓰인 값이면 뒤에 선다).
 */
export const SAMPLE_TYPE_HINTS: readonly string[] = ['테스트', '검증', '기타']

/** 고정 제안의 화면 표기. 🔴 위 배열의 값 자체는 서버로 나가는 글자라 언어와 상관없이 그대로다 — 번역은 보이는 글자에만 건다 */
const SAMPLE_TYPE_HINT_LABEL: ReadonlyMap<string, MessageKey> = new Map([
  ['테스트', 'sampleNew.hints.type.test'],
  ['검증', 'sampleNew.hints.type.validation'],
  ['기타', 'sampleNew.hints.type.other'],
])

/** 시료 타입 제안 → 화면 표기. 고정 제안이 아니면(쓰인 값 · 직접 입력) 받은 글자 그대로다 */
export function sampleTypeHintLabel(value: string): string {
  const key = SAMPLE_TYPE_HINT_LABEL.get(value)
  return key === undefined ? value : t(key)
}

/**
 * 암 병기 — ⓑ 닫힌 표준. AJCC(TNM) 병기 군의 네 단(I–IV). 세부(`IA` · `IIIB` …)는 직접 적는다.
 * 🔵 표기는 **이미 등록된 시료가 쓰는 표기**를 따른다(`stageHints`) — 로마 숫자가 기본이다.
 * 정본(`schema.sql` `cancer_stage`)은 자유 TEXT 이고 표기를 정하지 않았다.
 */
export const CANCER_STAGE_ROMAN: readonly string[] = ['I', 'II', 'III', 'IV']
const CANCER_STAGE_ARABIC: readonly string[] = ['1', '2', '3', '4']

/** 제안 목록 길이 상한 — 한 화면에 훑을 만큼만 */
const HINT_CAP = 20

/**
 * 이미 등록된 시료에서 쓰인 값 — 많이 쓴 것부터 · 빈 값 제외.
 * `split` 이면 한 칸에 합쳐 적힌 여러 값(「위암, 폐암」 · 2026-10-04 암종 · 병기 여러 개)을 낱낱으로 센다.
 */
function usedValues(
  samples: readonly Sample[],
  pick: (sample: Sample) => unknown,
  split = false,
): string[] {
  const counts = new Map<string, number>()
  for (const sample of samples) {
    const raw = pick(sample)
    if (typeof raw !== 'string') continue
    for (const value of split ? splitTags(raw) : [raw.trim()]) {
      if (value === '') continue
      counts.set(value, (counts.get(value) ?? 0) + 1)
    }
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([value]) => value)
}

/** 고정 제안을 앞에, 쓰인 값을 뒤에 — 겹치면 한 번만 */
function merged(fixed: readonly string[], used: readonly string[]): string[] {
  return [...new Set([...fixed, ...used])].slice(0, HINT_CAP)
}

/**
 * 병기 제안 표기 — 등록된 병기가 **아라비아 숫자로만** 적혀 있으면 1–4, 아니면 I–IV.
 * 「이미 쓰는 표기에 맞춘다」 — 한 DB 안에서 `III` 과 `3` 이 섞이면 학습 입력이 갈린다.
 */
function stageHints(used: readonly string[]): readonly string[] {
  const arabic = used.some((v) => /^[1-4]/.test(v))
  const roman = used.some((v) => /^(IV|I{1,3})/i.test(v))
  return arabic && !roman ? CANCER_STAGE_ARABIC : CANCER_STAGE_ROMAN
}

export interface SampleHints {
  sampleType: string[]
  cancerType: string[]
  cancerStage: string[]
}

export function sampleHints(samples: readonly Sample[]): SampleHints {
  const usedStages = usedValues(samples, (s) => s.cancer_stage, true)
  return {
    sampleType: merged(
      SAMPLE_TYPE_HINTS,
      usedValues(samples, (s) => s.sample_type),
    ),
    // 암종은 고정 목록이 없다 — 정본에 목록이 없고, 지어내면 「🔸」 위반이다
    cancerType: merged(
      [],
      usedValues(samples, (s) => s.cancer_type, true),
    ),
    cancerStage: merged(stageHints(usedStages), usedStages),
  }
}

/* ───────────── 직접 입력한 값 기억 ───────────── */

export type HintField = keyof SampleHints

/**
 * 「+ 직접 입력」으로 적은 값은 이 브라우저에 남겨 다음에 목록에 보인다(eric 2026-10-21 「모니터링 보기 체크박스와 같이.. 브라우저 캐시에
 * 저장하게.. 캐시를 지우지 않는 한」). 영속 저장(`local`) — 자격증명이 아니라 입력 편의 값이다. 서버에는 보내지 않는다
 */
export const CUSTOM_HINTS_KEY = 'ctcells:sampleNew:customHints'
const CUSTOM_CAP = 30
/** 숨긴 값 상한 — 사용자가 × 로 뺀 것만 쌓인다 */
const HIDDEN_CAP = 100

/**
 * 이 브라우저의 목록 편집 — 직접 입력으로 기억한 값(칸별 배열)과 × 로 뺀 값(`hidden`).
 * 🔵 숨김은 «등록된 시료에서 쓰인 값 · 고정 제안»처럼 서버 · 코드에 있어 지울 수 없는 값을 이 브라우저에서만 안 보이게 한다
 *    (eric 2026-10-21 「x 버튼 오른쪽에 넣어서.. 이미 저장된거 뺄수도 잇게」). 서버의 시료는 그대로다
 */
export type CustomHints = Record<HintField, string[]> & { hidden: Record<HintField, string[]> }

export function readCustomHints(): CustomHints {
  const raw = safeStorage.readJson<Partial<Record<HintField | 'hidden', unknown>>>(CUSTOM_HINTS_KEY)
  const list = (v: unknown): string[] =>
    Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && x.trim() !== '') : []
  const hiddenRaw =
    typeof raw?.hidden === 'object' && raw.hidden !== null
      ? (raw.hidden as Partial<Record<HintField, unknown>>)
      : {}
  return {
    sampleType: list(raw?.sampleType),
    cancerType: list(raw?.cancerType),
    cancerStage: list(raw?.cancerStage),
    hidden: {
      sampleType: list(hiddenRaw.sampleType),
      cancerType: list(hiddenRaw.cancerType),
      cancerStage: list(hiddenRaw.cancerStage),
    },
  }
}

function writeCustomHints(next: CustomHints): CustomHints {
  safeStorage.writeJson(CUSTOM_HINTS_KEY, next)
  return next
}

/** 적은 값을 맨 앞에 얹어(최근 먼저 · 겹치면 앞으로) 저장하고, 새 묶음을 돌려준다. 숨겼던 값이면 숨김에서 푼다 */
export function addCustomHint(field: HintField, value: string): CustomHints {
  const v = value.trim()
  const cur = readCustomHints()
  if (v === '') return cur
  return writeCustomHints({
    ...cur,
    [field]: [v, ...cur[field].filter((x) => x !== v)].slice(0, CUSTOM_CAP),
    hidden: { ...cur.hidden, [field]: cur.hidden[field].filter((x) => x !== v) },
  })
}

/**
 * 목록에서 뺀다(× · eric 2026-10-21) — 기억한 값이면 기억에서 지우고, 같은 값이 등록된 시료 · 고정 제안에도 있을 수 있으니
 * 숨김에도 넣는다(안 넣으면 기억에서만 빠지고 «쓰인 값»으로 되살아나 보인다). 칸의 값은 건드리지 않는다
 */
export function hideHint(field: HintField, value: string): CustomHints {
  const v = value.trim()
  const cur = readCustomHints()
  if (v === '') return cur
  return writeCustomHints({
    ...cur,
    [field]: cur[field].filter((x) => x !== v),
    hidden: {
      ...cur.hidden,
      [field]: [v, ...cur.hidden[field].filter((x) => x !== v)].slice(0, HIDDEN_CAP),
    },
  })
}

/** 기억한 값을 목록 앞에 — 등록된 값 · 고정 제안과 겹치면 한 번만. 숨긴 값은 뺀다 */
export function withCustomHints(hints: SampleHints, custom: CustomHints): SampleHints {
  const join = (field: HintField) => {
    const hidden = new Set(custom.hidden[field])
    return [...new Set([...custom[field], ...hints[field]])].filter((v) => !hidden.has(v))
  }
  return {
    sampleType: join('sampleType'),
    cancerType: join('cancerType'),
    cancerStage: join('cancerStage'),
  }
}
